// Generated by GitHub Copilot
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import UserList from "./UserList/UserList";
import CreateUserModal from "./UserForm/CreateUserModal";
import RefreshIndicator from "../Modern/Incidents/components/RefreshIndicator";
import usePolling from "../../hooks/usePolling";
import { POLLING_INTERVALS } from "../../config/polling";
import styles from "./UserManagementApp.module.css";
import { FaUsers, FaUserPlus, FaTimes, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { trackUserManagementAccess, trackUserModalInteraction } from "../../utils/modernUITracking";

/**
 * Main User Management Application Component
 * Accessible only to users with deity privileges
 */
const UserManagementApp = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-clear error message after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Check deity privilege on component mount
  useEffect(() => {
    if (!user || !user.privileges || !user.privileges.deity) {
      // Track access denied
      trackUserManagementAccess(false, {
        adminEmail: user?.email || 'unknown',
        reason: 'missing_deity_privilege'
      });
      setError("Access denied. Deity privileges required.");
      setLoading(false);
      return;
    }

    // Track successful access
    trackUserManagementAccess(true, {
      adminEmail: user.email
    });

    fetchUsers();
  }, [user]);

  // Setup background polling (only when deity privileges exist and initial load complete)
  usePolling(
    () => fetchUsers(true),
    POLLING_INTERVALS.USER_MANAGEMENT,
    {
      enabled: !isInitialLoad && !loading && user?.privileges?.deity,
      pauseWhenHidden: true
    }
  );

  /**
   * Fetch all users from the API
   * @param {boolean} isBackgroundRefresh - If true, uses refreshing state instead of loading state
   */
  const fetchUsers = async (isBackgroundRefresh = false) => {
    try {
      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Only clear errors on initial load, not background refresh
      if (!isBackgroundRefresh) {
        setError(null);
      }

      const response = await fetch("/api/v1/config/users", {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch users: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      // API returns a top-level array of users, not an object with users property
      setUsers(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
      setIsInitialLoad(false);
    } catch (error) {
      console.error("Error fetching users:", error);

      // On background refresh errors, keep showing old data and just log warning
      if (isBackgroundRefresh) {
        console.warn("Background refresh failed, keeping existing data:", error.message);
      } else {
        // On initial load errors, show error to user
        setError(`Failed to load users: ${error.message}`);
      }
    } finally {
      if (isBackgroundRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  /**
   * Handle user creation
   */
  const handleUserCreated = (newUser) => {
    setUsers((prevUsers) => [...prevUsers, newUser]);
    setSuccess(`User ${newUser.email} created successfully`);
    setShowCreateModal(false); // Close modal
  };

  /**
   * Handle user updates
   */
  const handleUserUpdated = (updatedUser) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.email === updatedUser.email ? updatedUser : user,
      ),
    );
    setSuccess(`User ${updatedUser.email} updated successfully`);
  };

  /**
   * Handle user deletion
   */
  const handleUserDeleted = (deletedUserEmail) => {
    setUsers((prevUsers) =>
      prevUsers.filter((user) => user.email !== deletedUserEmail),
    );
    setSuccess(`User ${deletedUserEmail} deleted successfully`);
  };

  // Access denied for non-deity users
  if (!user || !user.privileges || !user.privileges.deity) {
    return (
      <div className={styles.container}>
        <div className={`${styles.alert} ${styles.alertError}`}>
          <div className={styles.alertIcon}>
            <FaExclamationCircle />
          </div>
          <div className={styles.alertContent}>
            <h3 className={styles.alertHeading}>Access Denied</h3>
            <p className={styles.alertMessage}>
              You must have deity privileges to access the User Management
              interface. Please contact your administrator if you believe you
              should have access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitleRow}>
            <div className={styles.headerIcon}>
              <FaUsers />
            </div>
            <h1 className={styles.title}>User Management</h1>
            <RefreshIndicator
              isRefreshing={refreshing}
              lastUpdated={lastUpdated}
              showTimestamp={!refreshing && !!lastUpdated}
            />
          </div>
        </div>
        <div className={styles.actions}>
          <button
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={() => {
              trackUserModalInteraction('create', 'open', {
                adminEmail: user.email
              });
              setShowCreateModal(true);
            }}
            aria-label="Create new user"
          >
            <FaUserPlus className={styles.buttonIcon} />
            <span>Create User</span>
          </button>
        </div>
      </div>

      {error && (
        <div className={`${styles.alert} ${styles.alertError}`}>
          <div className={styles.alertIcon}>
            <FaExclamationCircle />
          </div>
          <div className={styles.alertContent}>
            <p className={styles.alertMessage}>{error}</p>
          </div>
          <button
            className={styles.alertDismiss}
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {success && (
        <div className={`${styles.alert} ${styles.alertSuccess}`}>
          <div className={styles.alertIcon}>
            <FaCheckCircle />
          </div>
          <div className={styles.alertContent}>
            <p className={styles.alertMessage}>{success}</p>
          </div>
          <button
            className={styles.alertDismiss}
            onClick={() => setSuccess(null)}
            aria-label="Dismiss success"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} role="status" aria-label="Loading"></div>
          <p className={styles.loadingText}>Loading user data...</p>
        </div>
      ) : (
        <UserList
          users={users}
          onUserUpdated={handleUserUpdated}
          onUserDeleted={handleUserDeleted}
          currentUser={user}
        />
      )}

      {/* Create User Modal */}
      <CreateUserModal
        show={showCreateModal}
        onClose={() => {
          trackUserModalInteraction('create', 'close', {
            adminEmail: user.email
          });
          setShowCreateModal(false);
        }}
        onUserCreated={handleUserCreated}
        currentUser={user}
      />
    </div>
  );
};

UserManagementApp.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string.isRequired,
    privileges: PropTypes.object.isRequired
  }).isRequired
};

export default UserManagementApp;
